import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import {
    IconChevronLeft,
    IconChevronRight,
    IconFlame,
    IconClock,
    IconBallFootball,
    IconBallBasketball,
} from '@tabler/icons-react';

const LEAGUES = [
    { id: 'tur.1', sport: 'soccer', path: 'soccer/tur.1' },
    { id: 'eng.1', sport: 'soccer', path: 'soccer/eng.1' },
    { id: 'esp.1', sport: 'soccer', path: 'soccer/esp.1' },
    { id: 'ita.1', sport: 'soccer', path: 'soccer/ita.1' },
    { id: 'ger.1', sport: 'soccer', path: 'soccer/ger.1' },
    { id: 'fra.1', sport: 'soccer', path: 'soccer/fra.1' },
    { id: 'uefa.champions', sport: 'soccer', path: 'soccer/uefa.champions' },
    { id: 'uefa.europa', sport: 'soccer', path: 'soccer/uefa.europa' },
    { id: 'usa.1', sport: 'soccer', path: 'soccer/usa.1' },
    { id: 'nba', sport: 'basketball', path: 'basketball/nba' },
];

function scoreboardUrl(league, from, to) {
    return `https://site.api.espn.com/apis/site/v2/sports/${league.path}/scoreboard?dates=${from}-${to}`;
}

function orderedTeams(match) {
    const teams = match.competitions?.[0]?.competitors || [];
    const home = teams.find((t) => t.homeAway === 'home');
    const away = teams.find((t) => t.homeAway === 'away');
    if (home && away) return [home, away];
    return teams;
}

function isLive(match) {
    return match.status?.type?.state === 'in';
}

function isDone(match) {
    return Boolean(match.status?.type?.completed);
}

function statusLabel(match) {
    if (isDone(match)) return 'MS';
    if (isLive(match)) return match.status?.period ? `${match.status.displayClock || ''}`.trim() || 'Canlı' : 'Canlı';
    return dayjs(match.date).format('HH:mm');
}

function dateLabel(offset) {
    if (offset === 0) return 'Bugün';
    if (offset === 1) return 'Yarın';
    if (offset === -1) return 'Dün';
    return dayjs().add(offset, 'day').format('D MMM');
}

function dayChip(date) {
    const d = dayjs(date);
    const today = dayjs().startOf('day');
    const diff = d.startOf('day').diff(today, 'day');
    if (diff === 0) return 'Bugün';
    if (diff === 1) return 'Yarın';
    if (diff === -1) return 'Dün';
    return d.format('D MMM');
}

const MatchCard = ({ match }) => {
    const teams = orderedTeams(match);
    if (teams.length < 2) return null;
    const live = isLive(match);
    const SportIcon = match.sport === 'basketball' ? IconBallBasketball : IconBallFootball;
    const [home, away] = teams;

    return (
        <div className="shrink-0 flex items-center gap-2 h-9 px-3 rounded-full bg-white border border-[#d9dee7] shadow-[0_1px_0_rgba(16,24,40,0.04)]">
            <SportIcon size={14} stroke={1.6} className={live ? 'text-[#af2122]' : 'text-[#8b929c]'} />
            <span className={`text-[13px] font-semibold tabular-nums ${live ? 'text-[#af2122]' : 'text-[#111]'}`}>
                {statusLabel(match)}
            </span>
            <img
                src={home.team.logo}
                alt={home.team.shortDisplayName}
                title={home.team.shortDisplayName}
                className="w-5 h-5 object-contain"
                onError={(e) => {
                    e.target.style.display = 'none';
                }}
            />
            <span className="text-[#9aa0a6] text-[13px] leading-none">-</span>
            <img
                src={away.team.logo}
                alt={away.team.shortDisplayName}
                title={away.team.shortDisplayName}
                className="w-5 h-5 object-contain"
                onError={(e) => {
                    e.target.style.display = 'none';
                }}
            />
        </div>
    );
};

const ESPNMatches = () => {
    dayjs.locale('tr');
    const [dayOffset, setDayOffset] = useState(0);
    const [trending, setTrending] = useState(false);
    const [fixtures, setFixtures] = useState([]);
    const [ready, setReady] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setReady(false);
        const from = dayjs().add(dayOffset, 'day').format('YYYYMMDD');
        const to = dayjs().add(dayOffset + 7, 'day').format('YYYYMMDD');

        (async () => {
            const results = await Promise.all(
                LEAGUES.map(async (league) => {
                    try {
                        const res = await fetch(scoreboardUrl(league, from, to));
                        const data = await res.json();
                        return (data.events || []).map((event) => ({
                            ...event,
                            sport: league.sport,
                            league: league.id,
                        }));
                    } catch {
                        return [];
                    }
                })
            );
            if (cancelled) return;
            const merged = results
                .flat()
                .filter((match) => orderedTeams(match).length >= 2)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            setFixtures(merged);
            setReady(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [dayOffset]);

    const visible = trending
        ? [...fixtures].sort((a, b) => Number(isLive(b)) - Number(isLive(a)) || new Date(a.date) - new Date(b.date))
        : fixtures;

    const scrollBy = (dir) => {
        scrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };

    let lastDay = null;

    return (
        <div className="w-full bg-[#e8edf3] border-b border-[#dce3eb]">
            <div className="flex items-center gap-2 h-[52px] px-2 md:px-3">
                <button
                    type="button"
                    onClick={() => setTrending((v) => !v)}
                    className={`shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] font-semibold text-white ${
                        trending ? 'bg-[#d9480f]' : 'bg-[#374df5] hover:bg-[#2f42d6]'
                    }`}
                >
                    <IconFlame size={14} />
                    Trendler
                </button>

                <div className="shrink-0 flex items-center text-[#222]">
                    <button
                        type="button"
                        onClick={() => setDayOffset((v) => v - 1)}
                        className="h-8 w-6 flex items-center justify-center rounded hover:bg-[#f3f4f6]"
                        aria-label="Önceki gün"
                    >
                        <IconChevronLeft size={16} />
                    </button>
                    <span className="min-w-[48px] text-center text-[12px] font-semibold">
                        {dateLabel(dayOffset)}
                    </span>
                    <button
                        type="button"
                        onClick={() => setDayOffset((v) => v + 1)}
                        className="h-8 w-6 flex items-center justify-center rounded hover:bg-[#f3f4f6]"
                        aria-label="Sonraki gün"
                    >
                        <IconChevronRight size={16} />
                    </button>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-x-auto scrollbar-hide">
                    <div className="flex items-center gap-1.5 min-h-[46px] py-1">
                        {!ready && (
                            <span className="text-[12px] text-[#9ca3af] px-2">Yükleniyor…</span>
                        )}
                        {ready && visible.length === 0 && (
                            <span className="text-[12px] text-[#9ca3af] px-2">Maç bulunamadı</span>
                        )}
                        {visible.map((match) => {
                            const chip = dayChip(match.date);
                            const showChip = lastDay !== chip;
                            lastDay = chip;
                            return (
                                <React.Fragment key={match.id}>
                                    {showChip && dayOffset === 0 && chip !== 'Bugün' && (
                                        <div className="shrink-0 pl-2 pr-1 text-[11px] font-semibold text-[#6b7280] whitespace-nowrap">
                                            {chip}
                                        </div>
                                    )}
                                    <MatchCard match={match} />
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                <div className="hidden sm:flex shrink-0 items-center pl-1">
                    <button
                        type="button"
                        onClick={() => setDayOffset(1)}
                        className="h-8 px-2 text-[12px] font-semibold text-[#222] hover:bg-[#f3f4f6] rounded"
                    >
                        Yarın
                    </button>
                    <IconClock size={15} className="text-[#9aa0a6] mx-0.5" />
                    <button
                        type="button"
                        onClick={() => scrollBy(1)}
                        className="h-8 w-7 flex items-center justify-center rounded hover:bg-[#f3f4f6] text-[#222]"
                        aria-label="Daha fazla maç"
                    >
                        <IconChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ESPNMatches;
